import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FiX, FiShare2, FiLink } from "react-icons/fi";
import ConversationHistory from "../../components/AiChatComponent/hooks/ConversationalHistory";
import AiChatBody from "../../components/AiChatComponent/panelComponents/AiChatBody";
import { Skeleton } from "../../components/Skeleton";
import AiChatInput, { CHAT_INPUT_MEASURE } from "../../components/AiChatComponent/panelComponents/AiChatInput";
import { MentionEditorHandle } from "../../components/AiChatComponent/panelComponents/MentionEditor";
import AgentCommandMenu from "../../components/AiChatComponent/panelComponents/AgentCommandMenu";
import { type ScheduleItem, type ScheduleQuestion, type ScheduleOccurrence } from "../../utils/scheduleTransform";
import TokenLimitBanner from "../../components/AiChatComponent/panelComponents/TokenLimitBanner";
import ConnectionStatusBanner from "../../components/AiChatComponent/panelComponents/ConnectionStatusBanner";
import useAiChat from "../../components/AiChatComponent/hooks/useAiChat";
import useAtMention, { MentionedAsset } from "../../components/AiChatComponent/hooks/useAtMention";
import { formatPersonaForPrompt } from "../../utils/persona";
import { buildAssetContext } from "../../utils/assetContext";
import PromptDebugPanel from "./PromptDebugPanel";
import OrgChatAccessOverlay from "./OrgChatAccessOverlay";
import { getOrgChatAccessStatus } from "../../utils/termsAuditTrail";
import { PageContainer, PageHeader } from "../../components/AssetBreadCrumb";
import { PeekRail, PeekRailToggle, usePeekRail } from "../../components/ReusableComponents/PeekRail";
import { consumeMarketingPrompt } from "../../utils/marketingParams";
import { MCPService, ChatModelId } from "../../services/mcpService";
import { getUsageStatus, UsageStatus } from "../../services/mcpUsageService";
import { useAllMessagesSubscription, MCPMessage } from "../../hooks/useAllMessagesSubscription";
import { useMessageTimeout } from "../../hooks/useMessageTimeout";
import { useVisibilityRefetch } from "../../hooks/useVisibilityRefetch";
import { useKeyboardLift } from "../../hooks/useKeyboardLift";
import { addAiMessageFromSubscription, updateAiMessageFromSubscription, selectConversation, markConversationAsRead } from "../../store/aiChatSlice";
import { AppDispatch, RootState } from "../../store";
import { setModalState, modalViewsConst, clearLastResult } from "../../store/modalSlice";
import { fetchSystemPrompts } from "../../store/systemPromptSlice";
// import ChecklistEdgeTab from "../../components/Checklist/ChecklistEdgeTab";
import { useTour } from "../../components/Tour/TourProvider";
import { shouldSendOnboardingGuidance, setTourFlagStored } from "../../components/Tour/tourFlags";
// Hidden for now — preserved for potential future use import PersonaNudgeEdgeTab from "../../components/PersonaNudge/PersonaNudgeEdgeTab";

// This component represents the AI Analyst Page with chat functionality It includes conversation history, chat body, and input components.

/** Remembers whether the conversation sidebar was left pinned open. */
const SIDEBAR_PIN_KEY = "erode.aiAnalyst.sidebarPinned";

// Main AiAnalystPage component
const AiAnalystPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId: urlConversationId } = useParams<{ conversationId?: string }>();

  const [sidebarTab, setSidebarTab] = useState<'chat' | 'agent' | undefined>(undefined);
  // Hover peeks, click pins, and below lg the same control opens a sheet — shared with the asset page
  const rail = usePeekRail(SIDEBAR_PIN_KEY);
  const modalLastResult = useSelector((state: RootState) => state.modalState.lastResult);
  /** Resets when leaving the page so the banner shows again on the next visit. */
  const [showPreviewBanner, setShowPreviewBanner] = useState(true);
  // Phone keyboard: the chat card rises until the input sits on it, so the empty state / last message come along; the rest of the app stays put
  const inputAreaRef = useRef<HTMLDivElement>(null);
  const chatCardRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  useKeyboardLift(inputAreaRef, chatCardRef, chatScrollRef);
  const [usageStatus, setUsageStatus] = useState<UsageStatus | null>(null);
  const [scheduleView, setScheduleView] = useState<boolean>(false);
  const [usageRefreshToken, setUsageRefreshToken] = useState(0);
  const tour = useTour();
  const warmedModelsRef = useRef<Set<ChatModelId>>(new Set());
  const prevUrlRef = useRef<string | undefined>(undefined);
  /** A question handed over ready-to-send (e.g. from the operations dashboard), held until the input has it. */
  const pendingAutoSendRef = useRef<string | null>(null);
  const {
    mentionedAssets,
    isPickerOpen: isMentionPickerOpen,
    mentionQuery,
    filteredAssets: mentionSuggestions,
    handleInputChange: mentionHandleInputChange,
    registerMention,
    resolveEntity,
    clearMentions,
    closePicker,
    restoreMentions,
  } = useAtMention();
  const editorRef = useRef<MentionEditorHandle>(null);
  const mentionedAssetsRef = useRef(mentionedAssets);
  mentionedAssetsRef.current = mentionedAssets;
  // Assets-by-group dict, read at send time to list a mentioned group's member assets in the AI context block.
  const dataGroups = useSelector((state: RootState) => state.assets.dataGroups as Record<string, { name: string }[]>);
  const dataGroupsRef = useRef(dataGroups);
  dataGroupsRef.current = dataGroups;
  const mentionDraftsRef = useRef<Record<string, typeof mentionedAssets>>({});
  const prevConversationIdRef = useRef<string | undefined>(undefined);
  const loadingFromUrlRef = useRef(false);
  const user = useSelector((state: any) => state.user);
  const userId = user?.permissionDetails?.id ?? null;
  const isAdmin = user?.details?.groups?.includes("Admin") ?? false;
  const orgChatAccess = useMemo(
    () =>
      getOrgChatAccessStatus({
        isAdmin,
        organizationsLoaded: user?.organizations?.loaded ?? false,
        organizations: user?.organizations?.data,
      }),
    [isAdmin, user?.details?.groups, user?.organizations?.loaded, user?.organizations?.data]
  );
  const preferredPersonaId = user?.permissionDetails?.preferredPersonaId ?? null;
  const personas = useSelector((state: any) => state.personas?.personas ?? {});
  const activePersona = preferredPersonaId ? personas[preferredPersonaId] : null;
  const handleLimitExceeded = (status: UsageStatus) => {
    setUsageStatus(status);
  };

  const handleNoSubscription = () => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.noSubscription,
        properties: { __modalSize: "sm" },
      })
    );
  };

  const handleScheduleResultSelect = (item: ScheduleItem, question: ScheduleQuestion, occurrence: ScheduleOccurrence) => {
    // Load the full MCPConversation — occurrence.id is the conversationId
    dispatch(selectConversation(occurrence.id));
    setScheduleView(true);
    rail.dismiss();
  };

  const handleTabChange = (tab: 'chat' | 'agent') => {
    if (tab === 'chat') setScheduleView(false);
  };

  // Format active persona for system prompt
  const {
    inputValue,
    setInputValue,
    messages,
    isLoading,
    error,
    currentConversation,
    isFetchingConversation,
    model,
    modelOverride,
    setModelOverride,
    messagesEndRef,
    inputRef,
    handleConversationSelect,
    handleNewConversation,
    handleSend,
    handleRetry,
    handleKeyDown,
    attachedFiles,
    isUploading,
    handleFileSelect,
    handleRemoveFile,
    activeConversationTokens,
    agentMenuOpen,
    filteredCommands,
    handleAgentCommandSelect,
  } = useAiChat({
    onHistoryClose: rail.dismiss,
    userId,
    isAdmin,
    onUsageRecorded: () => setUsageRefreshToken((prev) => prev + 1),
    onLimitExceeded: handleLimitExceeded,
    onNoSubscription: handleNoSubscription,
    persona: formatPersonaForPrompt(activePersona),
    focusInput: () => editorRef.current?.focus(),
    // Onboard once per user: during the chat tour, on a brand-new user's first-ever message (deferred, if they skipped the tour), or when a not-yet-established user sounds new.
    resolveOnboarding: (ctx) =>
      shouldSendOnboardingGuidance({ userId, tourActive: tour.activeTour === "chat", ...ctx }),
    onOnboardingSent: () => setTourFlagStored(userId, "onboardingGuidanceSent"),
    onBeforeSend: (value) => {
      const context = buildAssetContext(mentionedAssetsRef.current, dataGroupsRef.current);
      return context ? value + context : undefined;
    },
  });

  useEffect(() => {
    if (location.state?.initialInput) {
      setInputValue(location.state.initialInput);
      // A question asked from a dashboard was already sent there — arriving pre-filled would ask twice
      if (location.state.autoSend) {
        pendingAutoSendRef.current = location.state.initialInput;
        // Only an auto-sent question drops its state, so a refresh cannot re-send it; pre-fill callers keep theirs
        navigate(location.pathname, { replace: true, state: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only; re-running on `location` would replay the question
  }, []);

  // Let tour coachmark suggestions drop a starter prompt straight into the input — no typing.
  useEffect(
    () =>
      tour.registerSuggestionHandler((text) => {
        setInputValue(text);
        editorRef.current?.focus();
      }),
    [tour, setInputValue]
  );

  // Pre-fill from a marketing deep-link prompt (?prompt=...). Stashed in sessionStorage so it survives the Stripe checkout round-trip when a plan was also specified; consumed one-shot so it shows once.
  useEffect(() => {
    const prompt = consumeMarketingPrompt();
    if (prompt) {
      setInputValue(prompt);
    }
  }, []); // only run on mount

  // Handle subscription messages - add AI messages from real-time subscription
  const handleSubscriptionMessage = useCallback(
    (message: MCPMessage) => {
      dispatch(
        addAiMessageFromSubscription({
          conversationId: message.conversationId,
          message: {
            id: message.id,
            text: message.content,
            isUser: message.isUser,
            sessionId: message.sessionId,
            inputTokens: message.inputTokens,
            outputTokens: message.outputTokens,
            executionTimeMs: message.executionTimeMs,
            createdAt: message.createdAt,
            status: message.status,
          },
        })
      );
      // Refresh usage status after receiving AI message (tokens were used)
      setUsageRefreshToken((prev) => prev + 1);
    },
    [dispatch]
  );

  // Handle subscription updates - resolve a pending placeholder to the answer/error
  const handleSubscriptionUpdate = useCallback(
    (message: MCPMessage) => {
      dispatch(
        updateAiMessageFromSubscription({
          conversationId: message.conversationId,
          messageId: message.id,
          updates: {
            text: message.content,
            status: message.status,
            sessionId: message.sessionId,
            inputTokens: message.inputTokens,
            outputTokens: message.outputTokens,
            executionTimeMs: message.executionTimeMs,
            // Keeps a second tab in sync when the owner rates a response. Only forwarded when
            // present: the Lambda's own updates don't select these fields, so they arrive null.
            ...(message.rating ? { rating: message.rating } : {}),
            ...(message.ratingComment ? { ratingComment: message.ratingComment } : {}),
          },
        })
      );
      // Tokens were consumed once the reply lands
      if (message.status === "complete") {
        setUsageRefreshToken((prev) => prev + 1);
      }
    },
    [dispatch]
  );

  // Editor path: the Lexical editor reports the serialized value and the caret's absolute text offset on every change (typing, clicks, arrow moves).
  const handleEditorChange = useCallback(
    (value: string, caretOffset: number) => {
      mentionHandleInputChange(value, { selectionStart: caretOffset });
      setInputValue(value);
    },
    [mentionHandleInputChange, setInputValue]
  );

  const handleSendWithMentions = useCallback(async () => {
    // End the chat tour up front (before the async send) so the spotlight unmounts immediately — otherwise the optimistic message flips the page out of the empty state mid-await and the coachmark briefly reappears. onboarding is already captured in this render's handleSend closure, so ending now doesn't drop it.
    if (tour.activeTour === "chat") tour.end(true);
    clearMentions();
    if (currentConversation?.id) {
      delete mentionDraftsRef.current[currentConversation.id];
    }
    await handleSend();
  }, [handleSend, clearMentions, currentConversation?.id, tour]);

  // Waits for the input to hold the question, then sends through the same path as the Send button (tour end, mention cleanup)
  useEffect(() => {
    if (!pendingAutoSendRef.current || inputValue !== pendingAutoSendRef.current) return;
    pendingAutoSendRef.current = null;
    handleSendWithMentions();
  }, [inputValue, handleSendWithMentions]);

  // Share the whole conversation: open the share modal (link-only mode), which creates the share on mount.
  const handleShareConversation = useCallback(() => {
    const conversationId = currentConversation?.id;
    if (!conversationId) return;
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.shareEmbed,
        properties: { __modalSize: "sm", conversationId },
      })
    );
  }, [currentConversation?.id, dispatch]);

  // Picker selection: record the entity, then have the editor splice in the pill. The editor's onChange then delivers the updated serialized value.
  const handleMentionSelect = useCallback(
    (asset: MentionedAsset) => {
      registerMention(asset);
      editorRef.current?.insertMention(asset);
    },
    [registerMention]
  );

  // Subscribe to ALL new messages (auth filters to user's conversations) This allows receiving messages for ANY conversation, enabling unread notifications
  const { reconnect } = useAllMessagesSubscription(handleSubscriptionMessage, undefined, handleSubscriptionUpdate);

  // Handle message timeouts and tab visibility for subscription resilience
  useMessageTimeout();
  useVisibilityRefetch({
    currentConversationId: currentConversation?.id,
    onRefetch: (id) => dispatch(selectConversation(id)),
  });

  // Load conversation from URL parameter (for direct navigation or browser back/forward) IMPORTANT: Only react to URL changes, not currentConversation changes
  useEffect(() => {
    const urlChanged = prevUrlRef.current !== urlConversationId;
    prevUrlRef.current = urlConversationId;

    // Only load when URL actually changed (not when currentConversation changed)
    if (!urlChanged) {
      return;
    }

    // Skip if no URL param
    if (!urlConversationId) {
      return;
    }

    // Skip if already viewing this conversation — UNLESS we're still showing "Thinking..." (isLoading). On remount after navigating away, the live subscription was torn down and may have missed the reply, so reconcile against the DB to recover the resolved message and clear a stuck indicator.
    if (currentConversation?.id === urlConversationId && !isLoading) {
      return;
    }

    loadingFromUrlRef.current = true;
    dispatch(selectConversation(urlConversationId)).finally(() => {
      loadingFromUrlRef.current = false;
    });
  }, [urlConversationId, currentConversation?.id, isLoading, dispatch]);

  // Save/restore mention drafts when active conversation changes
  useEffect(() => {
    const prevId = prevConversationIdRef.current;
    const nextId = currentConversation?.id;

    if (prevId === nextId) return;

    if (prevId) {
      mentionDraftsRef.current[prevId] = mentionedAssetsRef.current;
    }

    restoreMentions(nextId ? (mentionDraftsRef.current[nextId] ?? []) : []);
    prevConversationIdRef.current = nextId;
  }, [currentConversation?.id, restoreMentions]);

  // Update URL when conversation changes
  useEffect(() => {
    if (!currentConversation?.id) {
      // No conversation - go to base URL only if there was one in the URL Guard: don't clear URL while we're actively loading a conversation from the URL
      if (urlConversationId && !isFetchingConversation && !loadingFromUrlRef.current) {
        navigate('/home/ai-analyst', { replace: true });
      }
      return;
    }

    // Update URL if conversation ID differs from what's in the URL
    if (currentConversation.id !== urlConversationId) {
      navigate(`/home/ai-analyst/${currentConversation.id}`, { replace: true });
    }
  }, [currentConversation?.id, urlConversationId, navigate, isFetchingConversation]);

  // Mark conversation as read when viewing it (persists to backend) TODO: Low priority - could optimize to only dispatch when unreadCount > 0 by checking conversations list in Redux. Current approach is idempotent but generates unnecessary mutations when conversation is already read.
  useEffect(() => {
    if (currentConversation?.id) {
      // selectConversation.fulfilled already zeroed the sidebar entry, so 0 is the true prior value to roll back to
      dispatch(markConversationAsRead({ conversationId: currentConversation.id, previousUnreadCount: 0 }));
    }
  }, [currentConversation?.id, dispatch]);

  useEffect(() => {
    if (warmedModelsRef.current.has(model)) {
      return;
    }
    warmedModelsRef.current.add(model);
    MCPService.warmup();
  }, [model]);

  // When the map draw modal closes with a KML result, construct a File and attach it
  useEffect(() => {
    if (modalLastResult?.type !== "chatMapDraw" || !modalLastResult?.kmlContent) return;
    const blob = new Blob([modalLastResult.kmlContent], {
      type: "application/vnd.google-earth.kml+xml",
    });
    const file = new File(
      [blob],
      modalLastResult.filename ?? "drawn-geometry.kml",
      { type: "application/vnd.google-earth.kml+xml" }
    );
    const dt = new DataTransfer();
    dt.items.add(file);
    handleFileSelect(dt.files);
    dispatch(clearLastResult());
  }, [modalLastResult, handleFileSelect, dispatch]);
  // Fetch system prompts for the admin PromptDebugPanel's DB editor. Admin-only: the prompt is resolved server-side (issue #694) and the SystemPrompt model is no longer readable by non-admins, so fetching here for everyone would just produce auth errors.
  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchSystemPrompts());
    }
  }, [dispatch, isAdmin]);

  // Fetch usage status on load and after a message is sent (onUsageRecorded bumps usageRefreshToken)
  useEffect(() => {
    if (!userId) {
      setUsageStatus(null);
      return;
    }
    getUsageStatus(userId, isAdmin).then(setUsageStatus);
  }, [userId, isAdmin, usageRefreshToken]);

  // A brand new conversation: no messages yet and not loading an existing one.
  const isNewConversation = messages.length === 0 && !isFetchingConversation;

  // Input area (banners + command menu + input). Centered under the empty-state heading for a new conversation, pinned to the bottom for an existing one. The tour anchor is only attached to the empty-state copy so exactly one `data-tour="chat-input"` element exists (the new-user tour runs on an empty chat).
  const renderInputArea = (tourAnchor?: string) => (
    <>
      <ConnectionStatusBanner onRetry={reconnect} />
      {usageStatus && <TokenLimitBanner usageStatus={usageStatus} />}
      {/* z-10 keeps the upward-opening / menu above the conversation */}
      <div ref={inputAreaRef} className="relative z-10">
        <AgentCommandMenu
          visible={agentMenuOpen}
          commands={filteredCommands}
          onSelect={handleAgentCommandSelect}
        />
        <AiChatInput
          variant="analyst"
          containerClassName=""
          dataTour={tourAnchor}
          inputRef={inputRef}
          inputValue={inputValue}
          onInputChange={setInputValue}
          onKeyDown={() => {}}
          onSend={handleSendWithMentions}
          isLoading={isLoading}
          error={error}
          disabled={usageStatus?.isLimitExceeded}
          disabledMessage="Token limit reached. Upgrade to continue chatting."
          attachedFiles={attachedFiles}
          isUploading={isUploading}
          onFileSelect={handleFileSelect}
          onRemoveFile={handleRemoveFile}
          onMapDraw={() => dispatch(setModalState({ modalOpen: true, modalView: modalViewsConst.chatMapDraw, properties: { __modalSize: "2xl" } }))}
          mentionedAssets={mentionedAssets}
          isMentionPickerOpen={isMentionPickerOpen}
          mentionQuery={mentionQuery}
          mentionSuggestions={mentionSuggestions}
          onMentionSelect={handleMentionSelect}
          onMentionPickerClose={closePicker}
          editorRef={editorRef}
          onEditorChange={handleEditorChange}
          resolveMentionEntity={resolveEntity}
        />
      </div>
    </>
  );

  const renderConversationHistory = (onOverlayClose?: () => void) => (
    <ConversationHistory
      currentConversationId={currentConversation?.id}
      onConversationSelect={handleConversationSelect}
      onNewConversation={handleNewConversation}
      embedded
      onOverlayClose={onOverlayClose}
      onScheduleResultSelect={handleScheduleResultSelect}
      activeTab={sidebarTab}
      onTabChange={(tab) => {
        setSidebarTab(undefined);
        handleTabChange(tab);
      }}
    />
  );

  const sharedLinksButton = (
    <button
      type="button"
      onClick={() =>
        dispatch(
          setModalState({
            modalOpen: true,
            modalView: modalViewsConst.manageSharedLinks,
            properties: { __modalSize: "md" },
          })
        )
      }
      className="font-outfit flex flex-none items-center gap-2 border-t border-eai-border px-4 py-3 text-eai-control text-eai-ink-muted transition-colors hover:bg-eai-surface-hover hover:text-eai-ink active:bg-eai-surface-active"
      title="Manage shared links"
      aria-label="Manage shared links"
    >
      <FiLink className="h-4 w-4" />
      <span>Shared links</span>
    </button>
  );

  // The product notice's contents. Rendered inline in the chat's control row where there is room for it, and as a strip under that row where there is not — one definition, two frames. Both frames take rounded-md, matching the app's status strips.
  const renderPreviewNotice = (messageClassName: string) => (
    <>
      <button
        type="button"
        className={`min-w-0 flex-1 text-left transition-colors hover:text-eai-ink-muted ${messageClassName}`}
        onClick={() =>
          dispatch(
            setModalState({
              modalOpen: true,
              modalView: modalViewsConst.provideFeedback,
              properties: {
                __modalSize: "md",
                conversationId: currentConversation?.id,
                userEmail: user?.permissionDetails?.email ?? user?.details?.email,
              },
            })
          )
        }
      >
        You&apos;re using the latest version of the Erode AI Analyst. Give feedback.
      </button>
      <button
        type="button"
        onClick={() => setShowPreviewBanner(false)}
        className="flex-none rounded-full p-0.5 text-eai-ink-muted transition-colors hover:bg-eai-warning-text/15 hover:text-eai-ink"
        aria-label="Dismiss notice"
      >
        <FiX className="h-3.5 w-3.5" aria-hidden />
      </button>
    </>
  );

  // Render the AI Analyst Page
  // max-sm: -mt-1/pb-1 trims the 20px top gap and grows the 8px bottom gap to match the 16px gutter
  return (
    <PageContainer fullHeight className="relative max-sm:-mt-1 max-sm:pb-1">
      {/* Org users can't use the chat until an admin enables access — page renders blurred behind the request-access overlay. While access is still loading we cover the page with skeleton placeholders (no blur gate), so approved users don't flash the access-required treatment but a blocked user still never sees an unprotected flash. */}
      {orgChatAccess.status !== "allowed" && <OrgChatAccessOverlay access={orgChatAccess} />}

      {/* sr-only under sm so the chat card gets the full height on phones but screen readers keep the h1 */}
      <PageHeader eyebrow="AI Analyst" className="mb-3 flex-none max-sm:sr-only" />

      <div ref={chatCardRef} className="min-h-0 flex-1 overflow-hidden rounded-eai-card border border-eai-border bg-eai-surface-card shadow-eai-card">
        <div className="relative flex h-full min-h-0 flex-col lg:flex-row">
          <PeekRail
            rail={rail}
            side="left"
            title="Conversations"
            footer={sharedLinksButton}
            peekHeaderClassName="h-11"
            testId="ai-sidebar"
          >
            {renderConversationHistory()}
          </PeekRail>

          {/* No stacking context here on purpose: the rail toggle lives in this column's control row but has to paint ON the peek panel beside it, which only works while both compete in the row's context. See PeekRail for the layers the two share. */}
          <section className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-eai-surface">
            {/* Controls sit ON the chat ground — no bar, no rule — so the chat reads as one surface. h-11 is the 32px toolbar rung plus its breathing room, not a panel header's 53px; the rail gets the same height so the toggle still lines up with the peek panel's eyebrow. */}
            {/* relative: the admin debug dropdown anchors absolute/top-full to this row */}
            <div className="relative flex h-11 flex-none items-center gap-2 px-3">
              <div className="flex flex-none items-center gap-2.5">
                <PeekRailToggle
                  rail={rail}
                  side="left"
                  label={rail.pinned ? "Hide conversations" : "Show conversations"}
                  data-testid="ai-sidebar-toggle"
                />
                <span className="hidden h-5 w-px bg-eai-border sm:block" />
                {/* Monthly usage — a fact, so it reads flat on the chat ground instead of as a pill */}
                {userId && (
                  <div className="font-outfit inline-flex items-center gap-2 text-eai-body-xs text-eai-ink-muted">
                    {usageStatus ? (
                      (() => {
                        const safeLimit = usageStatus.tokenLimit > 0 && Number.isFinite(usageStatus.tokenLimit) ? usageStatus.tokenLimit : 1;
                        const remainingPercent = Math.max(0, (safeLimit - usageStatus.tokensUsed) / safeLimit);
                        const usedPercent = Math.min(100, (usageStatus.tokensUsed / safeLimit) * 100);
                        const low = usageStatus.isLimitExceeded || remainingPercent <= 0.1;
                        return (
                          <>
                            <span className="hidden sm:inline">Usage</span>
                            <span
                              className="h-1.5 w-10 overflow-hidden rounded-full bg-eai-border sm:w-14"
                              title={`${usageStatus.tokensUsed.toLocaleString()} tokens used of ${usageStatus.tokenLimit.toLocaleString()} total`}
                            >
                              <span
                                className={`block h-full rounded-full transition-all ${
                                  low ? 'bg-eai-danger-solid' : remainingPercent <= 0.3 ? 'bg-eai-warning-text' : 'bg-eai-cta-primary'
                                }`}
                                style={{ width: `${usedPercent}%` }}
                              />
                            </span>
                            <span className={low ? 'text-eai-danger-text' : 'text-eai-ink'}>
                              {Math.round(remainingPercent * 100)}% left
                            </span>
                          </>
                        );
                      })()
                    ) : (
                      <Skeleton className="h-2.5 w-20 sm:w-28" />
                    )}
                  </div>
                )}
              </div>
              {/* The notice claims the gap between the two groups — lg and up, where that gap is real. Below lg it renders as a strip under this row instead. */}
              {showPreviewBanner && (
                <div
                  role="status"
                  className="font-inter mx-auto hidden h-eai-control-xs min-w-0 max-w-[560px] flex-1 items-center gap-2 rounded-md border border-eai-warning-text/25 bg-eai-brand-peach px-3 text-eai-body-xs text-eai-ink lg:flex"
                >
                  {renderPreviewNotice("truncate")}
                </div>
              )}

              <div className="ml-auto flex flex-none items-center gap-2 sm:gap-3">
                {activeConversationTokens > 0 && (
                  <span className="font-inter hidden text-eai-body-xs text-eai-ink-faint sm:inline">
                    {activeConversationTokens.toLocaleString()} tokens
                  </span>
                )}
                {currentConversation?.id && messages.length > 0 && (
                  <button
                    type="button"
                    onClick={handleShareConversation}
                    className="eai-btn-toolbar"
                    title="Share this conversation"
                    aria-label="Share this conversation"
                  >
                    <FiShare2 className="w-3.5 h-3.5" />
                    <span>Share</span>
                  </button>
                )}
                {user?.details?.groups?.includes("Admin") && (
                  <PromptDebugPanel
                    modelOverride={modelOverride}
                    onModelOverrideChange={setModelOverride}
                    persona={formatPersonaForPrompt(activePersona)}
                  />
                )}
              </div>
            </div>

            {showPreviewBanner && (
              <div
                role="status"
                className="font-inter mx-3 mb-1 flex flex-none items-center gap-2 rounded-md border border-eai-warning-text/25 bg-eai-brand-peach px-3 py-1.5 text-eai-body-xs leading-snug text-eai-ink lg:hidden"
              >
                {renderPreviewNotice("")}
              </div>
            )}

            {/* Chat body - FLEXIBLE MIDDLE */}
            <div ref={chatScrollRef} className="scrollable-div min-h-0 flex-1 overflow-y-auto px-3 sm:px-6">
              <div className="h-full">
                <AiChatBody
                  messages={messages}
                  isLoading={isLoading}
                  isFetchingConversation={isFetchingConversation}
                  messagesEndRef={messagesEndRef}
                  messagesClassName="mx-auto w-full max-w-[860px] space-y-2 pb-4 pt-4 sm:space-y-4 sm:pb-6 sm:pt-6"
                  hideMessagesWhenEmpty
                  emptyStateVariant="analyst"
                  conversationId={currentConversation?.id}
                  inputSlot={renderInputArea("chat-input")}
                  onRetry={handleRetry}
                />
              </div>
            </div>

            {/* Input area - pinned to the bottom when continuing an existing conversation. For a new conversation it renders centered under the empty-state heading (via inputSlot above). */}
            {!isNewConversation && (
              <div className="flex-none px-3 pt-2 sm:px-6 sm:py-3">
                <div className={`mx-auto min-h-[120px] w-full ${CHAT_INPUT_MEASURE}`}>
                  {renderInputArea()}
                </div>
              </div>
            )}
            {/* Forecasting club onboarding checklist hidden for now */}
            {/* <ChecklistEdgeTab /> */}
            {/* PersonaNudge hidden for now — preserved for potential future use */}
            {/* <PersonaNudgeEdgeTab /> */}
          </section>
        </div>
      </div>
    </PageContainer>
  );
};

export default AiAnalystPage;
